import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useSession } from 'next-auth/react';
import { SS_Set } from '../utils/local-storage';
import { useUser } from './UserProvider';
import { getFunnelContext, logError } from '../utils/error-tracking';
import { browserTimezone } from '../utils/timezone';
import { trackFunnelEvent, trackError } from '../utils/track-funnel';
import {
  GetNotificationsResponseSchema,
  UnreadCountsResponseSchema,
  NotificationMarkReadResponseSchema,
  ReadAllResponseSchema,
  DeleteNotificationResponseSchema,
} from '@/schemas/api/notifications';

export type NotificationType = 'success' | 'error' | 'warning' | 'info' | 'action';

export interface Notification {
  msg: string;
  type: NotificationType;
  actionURL?: string;
  dismissed: boolean;
  id?: string;
  timestamp?: number;
}

// Database notification interface
export interface DBNotification {
  id: string;
  userId: string;
  type: 'chat' | 'sale' | 'purchase' | 'transaction' | 'system';
  title: string;
  message: string;
  actionUrl: string | null;
  metadata: Record<string, unknown> | null;
  isRead: boolean;
  createdDate: Date;
  readDate: Date | null;
}

export interface NotificationContextType {
  notifications: Notification[];
  unreadMessagesCount: number;
  unreadSalesCount: number;
  unreadPurchasesCount: number;
  unreadTransactionsCount: number;
  systemNoticesCount: number;
  createNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void;
  dismissNotification: (index: number) => void;
  dismissAllNotifications: () => void;
  setUnreadMessagesCount: (count: number) => void;
  setUnreadSalesCount: (count: number) => void;
  setUnreadPurchasesCount: (count: number) => void;
  setUnreadTransactionsCount: (count: number) => void;
  setSystemNoticesCount: (count: number) => void;
  // New API-driven methods
  fetchNotifications: (options?: { type?: string; isRead?: boolean; limit?: number; offset?: number }) => Promise<{ notifications: DBNotification[]; total: number; hasMore: boolean }>;
  fetchUnreadCounts: () => Promise<void>;
  markNotificationRead: (notificationId: string) => Promise<void>;
  markAllNotificationsRead: (type?: string) => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [unreadSalesCount, setUnreadSalesCount] = useState(0);
  const [unreadPurchasesCount, setUnreadPurchasesCount] = useState(0);
  const [unreadTransactionsCount, setUnreadTransactionsCount] = useState(0);
  const [systemNoticesCount, setSystemNoticesCount] = useState(0);

  const createNotification = useCallback(({ msg, type, actionURL, dismissed = false }: Omit<Notification, 'id' | 'timestamp'>) => {
    const id = `notification-${Date.now()}-${Math.random()}`;
    const timestamp = Date.now();

    // Track error notifications in funnels
    if (type === 'error') {
      const funnelContext = getFunnelContext();

      // If we're in a funnel, track this error
      if (funnelContext.funnel_type === 'checkout' && funnelContext.checkout_session_id) {
        // Track checkout error with abandoned event
        trackFunnelEvent('checkout', 'abandoned', {
          error_type: 'notification_error',
          error_message: msg,
          error_severity: 'error',
        });
        // Also track as error for immediate sending
        trackError('checkout', 'notification_error', msg, {
          error_severity: 'error',
        });
      } else if (funnelContext.funnel_type === 'selling' && funnelContext.draft_session_id) {
        // Track selling error with abandoned event
        trackFunnelEvent('selling', 'abandoned', {
          error_type: 'notification_error',
          error_message: msg,
          error_severity: 'error',
        });
        // Also track as error for immediate sending
        trackError('selling', 'notification_error', msg, {
          error_severity: 'error',
        });
      } else if (funnelContext.funnel_type === 'auth' && funnelContext.flow_id) {
        // Track auth funnel errors
        trackFunnelEvent('login', 'otp_flow_failed', {
          event_step: funnelContext.funnel_step || 'unknown',
          error_type: 'notification_error',
          error_message: msg,
          error_severity: 'error',
        });
        // Also track as error for immediate sending
        trackError('login', 'notification_error', msg, {
          event_step: funnelContext.funnel_step || 'unknown',
          error_severity: 'error',
        });
      }
    }

    setNotifications(prev => {
      // Check for duplicate notifications that are NOT dismissed
      const activeDuplicate = prev.find(n => n.type === type && n.msg === msg && !n.dismissed);

      if (activeDuplicate) {
        // Notification already exists and is active - don't do anything
        return prev;
      }

      // Check if there's a dismissed duplicate - if so, remove it before adding new one
      const hasDismissedDuplicate = prev.some(n => n.type === type && n.msg === msg && n.dismissed);
      if (hasDismissedDuplicate) {
        // Remove dismissed duplicate before adding new one
        const filtered = prev.filter(n => !(n.type === type && n.msg === msg && n.dismissed));
        const newNotification: Notification = {
          id,
          msg,
          type,
          actionURL,
          dismissed,
          timestamp,
        };
        const updated = [newNotification, ...filtered];
        SS_Set('notifications', updated);
        return updated;
      }

      // Add new notification
      const newNotification: Notification = {
        id,
        msg,
        type,
        actionURL,
        dismissed,
        timestamp,
      };

      const updated = [newNotification, ...prev];
      SS_Set('notifications', updated);
      return updated;
    });

  }, []);

  useEffect(() => {
    // Set up event listener for notifications from other parts of the app
    const handleNotificationEvent = (event: CustomEvent) => {
      createNotification(event.detail);
    };

    // Set up event listener for error alerts from error tracking system
    const handleErrorAlertEvent = (event: CustomEvent) => {
      createNotification(event.detail);
    };

    // An effect only runs in the browser, so the document is always there.
    document.addEventListener('notification', handleNotificationEvent as EventListener);
    document.addEventListener('error-alert', handleErrorAlertEvent as EventListener);

    return () => {
      document.removeEventListener('notification', handleNotificationEvent as EventListener);
      document.removeEventListener('error-alert', handleErrorAlertEvent as EventListener);
    };
  }, [createNotification]);

  const dismissNotification = useCallback((index: number) => {
    setNotifications(prev => {
      const updated = prev.map((n, idx) => {
        if (idx === index) {
          return { ...n, dismissed: true };
        }
        return n;
      });
      SS_Set('notifications', updated);
      return updated;
    });

  }, []);

  const dismissAllNotifications = useCallback(() => {
    SS_Set('notifications', []);
    setNotifications([]);
  }, []);

  // Clean up dismissed notifications on route change (keep active ones)
  // Note: Disabled during SSG - will only work client-side if re-enabled with proper router check
  // useEffect(() => {
  //   if (typeof window === 'undefined') return;
  //   const router = require('next/router').useRouter();
  //   const handleRouteChange = () => {
  //     setNotifications(prev => {
  //       const activeNotifications = prev.filter(n => !n.dismissed);
  //       SS_Set('notifications', activeNotifications);
  //       return activeNotifications;
  //     });
  //   };
  //   router.events.on('routeChangeStart', handleRouteChange);
  //   return () => {
  //     router.events.off('routeChangeStart', handleRouteChange);
  //   };
  // }, []);

  // New API-driven methods - using session cookie authentication
  const { status } = useSession();
  const { loggedIn } = useUser();

  // Check both NextAuth session and legacy auth
  const isLoggedIn = status === 'authenticated' || loggedIn;

  const fetchNotifications = useCallback(async (options: { type?: string; isRead?: boolean; limit?: number; offset?: number } = {}) => {
    if (!isLoggedIn) {
      throw new Error('User not authenticated');
    }

    const params = new URLSearchParams();
    if (options.type) params.append('type', options.type);
    if (options.isRead !== undefined) params.append('isRead', String(options.isRead));
    if (options.limit) params.append('limit', String(options.limit));
    if (options.offset) params.append('offset', String(options.offset));

    const response = await fetch(`/api/notifications?${params.toString()}`, {
      credentials: 'include', // Include session cookie
    });

    if (!response.ok) {
      throw new Error('Failed to fetch notifications');
    }

    const rawData = await response.json();
    const parseResult = GetNotificationsResponseSchema.safeParse(rawData);
    if (!parseResult.success) {
      logError(new Error('fetchNotifications validation failed'), { action: 'fetchNotifications', component: 'NotificationProvider', extra: { zodErrors: parseResult.error.flatten() } });
      throw new Error('Invalid response format from notifications API');
    }

    const data = parseResult.data;
    if (!data.success) {
      throw new Error((data as { error?: string }).error || 'Failed to fetch notifications');
    }

    return {
      notifications: data.notifications as DBNotification[],
      total: data.total,
      hasMore: data.hasMore,
    };
  }, [isLoggedIn]);

  const fetchUnreadCounts = useCallback(async () => {
    if (!isLoggedIn) {
      return;
    }

    try {
      // The zone rides along on a call every logged-in page already makes,
      // rather than costing a request of its own. It is what tells the digest
      // crons which "9am" this reader meant; without it they fall back to UTC.
      // See utils/digest-timezone.ts for what the server does with it.
      const tz = browserTimezone();
      const url = tz
        ? `/api/notifications/unread-counts?tz=${encodeURIComponent(tz)}`
        : '/api/notifications/unread-counts';

      const response = await fetch(url, {
        credentials: 'include', // Include session cookie
      });

      if (!response.ok) {
        throw new Error('Failed to fetch unread counts');
      }

      const rawData = await response.json();
      const parseResult = UnreadCountsResponseSchema.safeParse(rawData);
      if (!parseResult.success) {
        logError(new Error('fetchUnreadCounts validation failed'), { action: 'fetchUnreadCounts', component: 'NotificationProvider', extra: { zodErrors: parseResult.error.flatten() } });
        return;
      }

      const data = parseResult.data;
      if (!data.success) {
        logError(new Error((data as { error?: string }).error || 'fetchUnreadCounts error'), { action: 'fetchUnreadCounts', component: 'NotificationProvider' });
        return;
      }

      const counts = data.counts;
      setUnreadMessagesCount(counts.chat || 0);
      setUnreadSalesCount(counts.sale || 0);
      setUnreadPurchasesCount(counts.purchase || 0);
      setUnreadTransactionsCount(counts.transaction || 0);
      setSystemNoticesCount(counts.system || 0);
    } catch (error) {
      logError(error instanceof Error ? error : new Error('Error fetching unread counts'), { action: 'fetchUnreadCounts', component: 'NotificationProvider' });
    }
  }, [isLoggedIn]);

  const markNotificationRead = useCallback(async (notificationId: string) => {
    if (!isLoggedIn) {
      throw new Error('User not authenticated');
    }

    const response = await fetch(`/api/notifications/${notificationId}/read`, {
      method: 'PUT',
      credentials: 'include', // Include session cookie
    });

    if (!response.ok) {
      throw new Error('Failed to mark notification as read');
    }

    const rawData = await response.json();
    const parseResult = NotificationMarkReadResponseSchema.safeParse(rawData);
    if (!parseResult.success) {
      logError(new Error('markNotificationRead validation failed'), { action: 'markNotificationRead', component: 'NotificationProvider', extra: { zodErrors: parseResult.error.flatten() } });
      throw new Error('Invalid response format from mark read API');
    }

    if (!parseResult.data.success) {
      throw new Error((parseResult.data as { error?: string }).error || 'Failed to mark notification as read');
    }

    // Refresh unread counts after marking as read
    await fetchUnreadCounts();
  }, [isLoggedIn, fetchUnreadCounts]);

  const markAllNotificationsRead = useCallback(async (type?: string) => {
    if (!isLoggedIn) {
      throw new Error('User not authenticated');
    }

    const params = type ? `?type=${type}` : '';
    const response = await fetch(`/api/notifications/read-all${params}`, {
      method: 'PUT',
      credentials: 'include', // Include session cookie
    });

    if (!response.ok) {
      throw new Error('Failed to mark notifications as read');
    }

    const rawData = await response.json();
    const parseResult = ReadAllResponseSchema.safeParse(rawData);
    if (!parseResult.success) {
      logError(new Error('markAllNotificationsRead validation failed'), { action: 'markAllNotificationsRead', component: 'NotificationProvider', extra: { zodErrors: parseResult.error.flatten() } });
      throw new Error('Invalid response format from mark all read API');
    }

    if (!parseResult.data.success) {
      throw new Error((parseResult.data as { error?: string }).error || 'Failed to mark notifications as read');
    }

    // Refresh unread counts after marking as read
    await fetchUnreadCounts();
  }, [isLoggedIn, fetchUnreadCounts]);

  const deleteNotification = useCallback(async (notificationId: string) => {
    if (!isLoggedIn) {
      throw new Error('User not authenticated');
    }

    const response = await fetch(`/api/notifications/${notificationId}`, {
      method: 'DELETE',
      credentials: 'include', // Include session cookie
    });

    if (!response.ok) {
      throw new Error('Failed to delete notification');
    }

    const rawData = await response.json();
    const parseResult = DeleteNotificationResponseSchema.safeParse(rawData);
    if (!parseResult.success) {
      logError(new Error('deleteNotification validation failed'), { action: 'deleteNotification', component: 'NotificationProvider', extra: { zodErrors: parseResult.error.flatten() } });
      throw new Error('Invalid response format from delete notification API');
    }

    if (!parseResult.data.success) {
      throw new Error((parseResult.data as { error?: string }).error || 'Failed to delete notification');
    }

    // Refresh unread counts after deleting
    await fetchUnreadCounts();
  }, [isLoggedIn, fetchUnreadCounts]);

  // Auto-fetch unread counts on mount and when login status changes
  useEffect(() => {
    if (isLoggedIn) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- fetchUnreadCounts is async and only updates count state after awaiting the /api/notifications/unread-counts response; this is a genuine data fetch on mount / auth-state change, not a synchronous cascading setState.
      void fetchUnreadCounts();
    }
  }, [isLoggedIn, fetchUnreadCounts]);

  const value: NotificationContextType = {
    notifications,
    unreadMessagesCount,
    unreadSalesCount,
    unreadPurchasesCount,
    unreadTransactionsCount,
    systemNoticesCount,
    createNotification,
    dismissNotification,
    dismissAllNotifications,
    setUnreadMessagesCount,
    setUnreadSalesCount,
    setUnreadPurchasesCount,
    setUnreadTransactionsCount,
    setSystemNoticesCount,
    fetchNotifications,
    fetchUnreadCounts,
    markNotificationRead,
    markAllNotificationsRead,
    deleteNotification,
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export default NotificationProvider;